import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import Recepcion from "@/pages/Recepcion";
import Laboratorio from "@/pages/Laboratorio";
import Correcciones from "@/pages/Correcciones";
import Produccion from "@/pages/Produccion";
import Trazabilidad from "@/pages/Trazabilidad";
import Calculadora from "@/pages/Calculadora";
import Search from "@/pages/Search";
import Parametros from "@/pages/Parametros";
import ComingSoon from "@/pages/ComingSoon";
import Inventario from "@/pages/Inventario";
import Ventas from "@/pages/Ventas";
import Clientes from "@/pages/Clientes";
import Cobranza from "@/pages/Cobranza";
import Proveedores from "@/pages/Proveedores";
import Compras from "@/pages/Compras";
import CuentasPorPagar from "@/pages/CuentasPorPagar";
import Retornables from "@/pages/Retornables";
import Embarques from "@/pages/Embarques";
import Tesoreria from "@/pages/Tesoreria";
import Nomina from "@/pages/Nomina";
import Checador from "@/pages/Checador";
import ReciboNomina from "@/pages/ReciboNomina";
import EntregaProducto from "@/pages/EntregaProducto";
import Usuarios from "@/pages/Usuarios";
import Auditoria from "@/pages/Auditoria";
import Perfil from "@/pages/Perfil";
import "@/App.css";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const Guard = ({ perm, children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "dueno") return children;
  if (!perm) return children;
  if (user.permissions?.[perm]?.view) return children;
  // Sin permiso: enviar a perfil (nunca a "/" para evitar loop si no tiene dashboard)
  return <Navigate to="/perfil" replace />;
};

/**
 * Detect session_id in URL fragment (returning from Emergent OAuth).
 * Must be done synchronously during render to avoid race conditions.
 */
function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/nomina/recibo/:lineId" element={<Guard perm="nomina"><ReciboNomina /></Guard>} />
      <Route path="/ventas/entrega/:saleId" element={<Guard perm="ventas"><EntregaProducto /></Guard>} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Guard perm="dashboard"><Dashboard /></Guard>} />
        {/* Operación */}
        <Route path="/recepcion" element={<Guard perm="recepcion"><Recepcion /></Guard>} />
        <Route path="/produccion" element={<Guard perm="produccion"><Produccion /></Guard>} />
        <Route path="/laboratorio" element={<Guard perm="laboratorio"><Laboratorio /></Guard>} />
        <Route path="/correcciones" element={<Guard perm="correcciones"><Correcciones /></Guard>} />
        <Route path="/trazabilidad" element={<Guard perm="trazabilidad"><Trazabilidad /></Guard>} />
        {/* Logística */}
        <Route path="/embarques" element={<Guard perm="embarques"><Embarques /></Guard>} />
        <Route path="/retornables" element={<Guard perm="retornables"><Retornables /></Guard>} />
        <Route path="/insumos" element={<Guard perm="insumos"><ComingSoon title="Insumos" description="Inventario de caseína, base, cuajo, sal y reactivos." items={["Entradas y salidas","Consumo por lote","Existencias en tiempo real"]} /></Guard>} />
        <Route path="/inventario" element={<Guard perm="inventario"><Inventario /></Guard>} />
        {/* Comercial */}
        <Route path="/clientes" element={<Guard perm="clientes"><Clientes /></Guard>} />
        <Route path="/ventas" element={<Guard perm="ventas"><Ventas /></Guard>} />
        <Route path="/proveedores" element={<Guard perm="proveedores"><Proveedores /></Guard>} />
        <Route path="/compras" element={<Guard perm="compras"><Compras /></Guard>} />
        <Route path="/cuentas-por-pagar" element={<Guard perm="cuentas_por_pagar"><CuentasPorPagar /></Guard>} />
        <Route path="/facturacion" element={<ComingSoon title="Facturación" description="Facturas, pagos parciales y saldos por cliente." />} />
        <Route path="/cobranza" element={<Guard perm="cobranza"><Cobranza /></Guard>} />
        <Route path="/tesoreria" element={<Guard perm="tesoreria"><Tesoreria /></Guard>} />
        <Route path="/nomina" element={<Guard perm="nomina"><Nomina /></Guard>} />
        <Route path="/checador" element={<Guard perm="checador"><Checador /></Guard>} />
        {/* Inteligencia */}
        <Route path="/calculadora" element={<Guard perm="calculadora"><Calculadora /></Guard>} />
        {/* Sistema */}
        <Route path="/parametros" element={<Guard perm="parametros"><Parametros /></Guard>} />
        <Route path="/usuarios" element={<Guard perm="usuarios"><Usuarios /></Guard>} />
        <Route path="/auditoria" element={<Guard perm="auditoria"><Auditoria /></Guard>} />
        <Route path="/perfil" element={<Perfil />} />
        <Route path="/buscar" element={<Search />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
